import { Avatar, Box, Button, Table, Text } from '@factorialco/deprecated-design-system'
import { PayrollIssueTableItemFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import UserAvatar from 'modules/core/components/UserAvatar'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import type { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'

import APP_ICON_ASSETS from 'modules/app_store/lib/appIconAssets'
import { DATEV_API_INTEGRATION_ID } from 'modules/marketplace/lib/integrationIds'
import {
  ADDITIONAL_COMPENSATION_ISSUE,
  INVALID_BANK_ACCOUNT_NUMBER,
  NO_BANK_ACCOUNT_NUMBER,
  PAYROLL_INTEGRATIONS_KOMBO_AUTHORIZATION_ISSUE,
  PAYROLL_INTEGRATIONS_KOMBO_MISSING_CONTRACT_TAXONOMY_CODE_ISSUE,
  PAYROLL_INTEGRATIONS_KOMBO_MISSING_EMPLOYEE_CODE_ISSUE,
  SUPPLEMENT_ISSUE,
} from 'modules/payroll_bundle/lib/constants'

type Issue = PayrollIssueTableItemFragment

const issueNavigation = (issue: Issue, navigation: Navigation<NavigationPaths>) => {
  switch (issue.issueType) {
    case INVALID_BANK_ACCOUNT_NUMBER:
    case NO_BANK_ACCOUNT_NUMBER:
    case PAYROLL_INTEGRATIONS_KOMBO_MISSING_EMPLOYEE_CODE_ISSUE:
      return navigation.employees.id.personal.fullPath({
        // This kind of issue should always have an employee
        id: issue.employee?.id.toString() ?? '',
      })
    case ADDITIONAL_COMPENSATION_ISSUE:
      return navigation.employees.id.contractVersions.fullPath({
        // This kind of issue should always have an employee
        id: issue.employee?.id.toString() ?? '',
      })
    case SUPPLEMENT_ISSUE:
      return navigation.payrollBundle.supplements.fullPath()
    case PAYROLL_INTEGRATIONS_KOMBO_AUTHORIZATION_ISSUE:
      return navigation.settings.payroll_bundle.integrationDatevApi.fullPath()
    case PAYROLL_INTEGRATIONS_KOMBO_MISSING_CONTRACT_TAXONOMY_CODE_ISSUE:
      return navigation.discover.marketplace.item.fullPath({ id: DATEV_API_INTEGRATION_ID })
    default:
      return ''
  }
}

const showUserAvatar = (issue: Issue) => {
  return [
    INVALID_BANK_ACCOUNT_NUMBER,
    NO_BANK_ACCOUNT_NUMBER,
    ADDITIONAL_COMPENSATION_ISSUE,
  ].includes(issue.issueType)
}

const appIconAsset = (issue: Issue): string => {
  switch (issue.issueType) {
    case PAYROLL_INTEGRATIONS_KOMBO_AUTHORIZATION_ISSUE:
    case PAYROLL_INTEGRATIONS_KOMBO_MISSING_EMPLOYEE_CODE_ISSUE:
    case PAYROLL_INTEGRATIONS_KOMBO_MISSING_CONTRACT_TAXONOMY_CODE_ISSUE:
      return APP_ICON_ASSETS['payroll_integrations_datev_api'] || ''
    default:
      return ''
  }
}

const appIconLabel = (issue: Issue): string => {
  switch (issue.issueType) {
    case PAYROLL_INTEGRATIONS_KOMBO_AUTHORIZATION_ISSUE:
    case PAYROLL_INTEGRATIONS_KOMBO_MISSING_EMPLOYEE_CODE_ISSUE:
    case PAYROLL_INTEGRATIONS_KOMBO_MISSING_CONTRACT_TAXONOMY_CODE_ISSUE:
      return i18n.t('app_store_v2.app_integrations.payroll_integrations_datev_api.general.title')
    default:
      return ''
  }
}

interface Props {
  issue: PayrollIssueTableItemFragment
}

const Item = ({ issue }: Props) => {
  const { navigation } = useNavigation()

  return (
    <Table.Row>
      <Table.Cell>
        <Box justifyContent="spaceBetween" flexDirection="row">
          <Box gap="s16" flexDirection="row">
            {issue.employee && showUserAvatar(issue) ? (
              <UserAvatar
                size="s40"
                user={{
                  avatar: issue.employee.access.avatar,
                  fullName: issue.employee.fullName,
                }}
              />
            ) : (
              <Avatar size="s40" url={appIconAsset(issue)} label={appIconLabel(issue)} />
            )}
            <Box>
              <Text.Normal weight="semibold">{issue.title}</Text.Normal>
              <Text.Normal>{issue.description}</Text.Normal>
            </Box>
          </Box>
          <Box textAlign="right">
            <Button.Router
              type="secondary"
              to={issueNavigation(issue, navigation)}
              iconName="arrow-right"
            />
          </Box>
        </Box>
      </Table.Cell>
    </Table.Row>
  )
}

export default observer(Item)
